import { ChangeEvent, ReactElement, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import { Box } from '@mui/material'
import { Modal } from 'components'
import Dropdown from 'components/ui/Dropdown/Dropdown'
import LoadingOverlay from 'components/ui/Loading/LoadingOverlay'
import { AppDispatch, RootState } from '../../../store'
import { ModalBody } from './styled'
import { CrudAction, Entity, usePermissions } from '../../../utils/hooks'
import { useNirService } from '../../../services'
import { TFlourWithId } from '../../../types/models/Flours'
import { setSelectedProduct } from '../../../store/slices/floursSlice'
import { AnalyticsListMode } from '../pages/list/Analytics'
import { useAnalyticsServices } from '../pages/services/services'
import { useSampleImporter } from '../../../components'

const useNewAnalysisModal = (
  mode?: AnalyticsListMode
): [ReactElement, () => void] => {
  const isBakersModule = mode === 'products'
  const { checkPermission } = usePermissions()
  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [fileSelected, setFileSelected] = useState<boolean>(false)
  const dispatch = useDispatch<AppDispatch>()
  const selectedProduct = useSelector(
    (state: RootState) => state.flours.selectedProduct
  )

  const { getProductsList } = useAnalyticsServices(isBakersModule)

  const { data: analysisProducts, isLoading } = useQuery({
    queryKey: ['analysis_products_list', isBakersModule],
    queryFn: () => getProductsList(),
    retry: false,
  })

  const handleSetProduct = (productId: string) => {
    dispatch(setSelectedProduct(productId))
  }

  const prefix = isBakersModule ? 'baker' : 'flour'
  const { uploadFileAndStream } = useNirService(prefix)
  const { handleUpload, fileInputRef } = useSampleImporter({
    uploadFileAndStream,
    onSuccessCallback: () => setIsOpen(false),
    prefix,
  })

  const [selectedFile, setSelectedFile] = useState<File | null>(null)

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setFileSelected(true)
    }
  }

  const handleProcessAnalysis = async () => {
    if (fileSelected && selectedFile) {
      setIsOpen(false)
      await handleUpload(selectedFile, {
        product_id: selectedProduct,
        post_aging: mode === 'post-aging',
      })
    } else {
      fileInputRef.current?.click()
    }
  }

  const productOptions =
    analysisProducts?.results?.map((code: TFlourWithId) => ({
      value: code.id.toString(),
      label: code.name,
    })) || []

  const toggleModal = () => {
    setIsOpen(true)
    setFileSelected(false)
    setSelectedFile(null)
  }

  return [
    <Box>
      <Modal
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false)
          setFileSelected(false)
          setSelectedFile(null)
        }}
        onConfirm={handleProcessAnalysis}
        isLoading={isLoading}
        title="New Analysis"
        confirmButtonDisabled={
          !selectedProduct || (fileSelected && !selectedFile)
        }
        onConfirmButtonText={fileSelected ? 'Process' : 'Upload File'}
      >
        <ModalBody>
          <Dropdown
            label="Select Product"
            options={productOptions}
            value={selectedProduct || undefined}
            onChange={(event) => {
              handleSetProduct(event.target.value)
            }}
            disabled={!checkPermission(Entity.PRODUCT, CrudAction.UPDATE)}
          />
        </ModalBody>
      </Modal>
      <LoadingOverlay open={isLoading} />
      <input
        hidden
        type="file"
        ref={fileInputRef}
        accept=".dx,.csv"
        onChange={handleFileChange}
      />
    </Box>,
    toggleModal,
  ]
}

export default useNewAnalysisModal
